import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { ModernDatePicker } from 'components/ui/ModernDatePicker'
import { organizationsApi, type Organization, type CreateOrganizationData } from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { Building2, Globe, Mail, Phone, MapPin, Link as LinkIcon, FileText, Lock, Briefcase, ChevronLeft, ChevronRight, Check, Sparkles, Eye, EyeOff, User, KeyRound, Send } from 'lucide-react'
import { Switch } from 'components/ui/switch'

interface CreateOrganizationModalProps {
  isOpen: boolean
  onClose: () => void
  onOrganizationCreated: (organization: Organization) => void
}

const STEPS = {
  BASIC_INFO: 0,
  CONTACT_INFO: 1,
  SUBSCRIPTION_LIMITS: 2,
  AI_SETTINGS: 3,
  ADMIN_ACCOUNT: 4
}

export function CreateOrganizationModal({ isOpen, onClose, onOrganizationCreated }: CreateOrganizationModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<CreateOrganizationData>({
    name: '',
    description: '',
    domain: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    number_of_users: 0,
    daily_cv_processing_limit: 0,
    daily_job_offer_limit: 0,
    subscription_plan: '',
    subscription_duration_type: 'days',
    subscription_duration_days: 0,
    subscription_deadline: undefined,
    ai_provider: 'ollama',
    ai_ollama_host: 'https://ollama.com/',
    ai_openrouter_host: 'https://openrouter.ai/api/v1',
    ai_model: 'qwen3-coder:480b-cloud',
    ai_model_ocr: 'devstral-small-2:24b-cloud',
    ai_token: '',
    use_default_ai_token: false,
    allow_external_cv_ai: false,
    system_email_language: 'fr',
    admin_email: '',
    admin_setup_mode: 'invitation',
    admin_password: '',
    admin_password_confirm: '',
    admin_name: ''
  })
  const [deadlineDate, setDeadlineDate] = useState<Date | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string>('')
  const [domainError, setDomainError] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')
  const [adminEmailError, setAdminEmailError] = useState<string>('')
  const [adminPasswordError, setAdminPasswordError] = useState<string>('')
  const [adminPasswordConfirmError, setAdminPasswordConfirmError] = useState<string>('')
  const [showAdminPassword, setShowAdminPassword] = useState(false)
  const [showAdminPasswordConfirm, setShowAdminPasswordConfirm] = useState(false)

  const totalSteps = 5
  const stepConfig = [
    { name: t('organizations.form.stepBasicInfo', 'Basic Information'), icon: Building2, description: t('organizations.form.stepBasicInfoDesc', 'Organization name and details') },
    { name: t('organizations.form.stepContactInfo', 'Contact Information'), icon: Mail, description: t('organizations.form.stepContactInfoDesc', 'Email, phone, and address') },
    { name: t('organizations.form.stepSubscription', 'Subscription & Limits'), icon: Briefcase, description: t('organizations.form.stepSubscriptionDesc', 'Limits and subscription settings') },
    { name: t('organizations.form.stepAiSettings', 'AI Settings'), icon: Sparkles, description: t('organizations.form.stepAiSettingsDesc', 'AI configuration and tokens') },
    { name: t('organizations.form.stepAdminAccount', 'Admin Account'), icon: Lock, description: t('organizations.form.stepAdminAccountDesc', 'Set up the organization admin account') }
  ]

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setCurrentStep(0)
      setFormData({
        name: '',
        description: '',
        domain: '',
        website: '',
        email: '',
        phone: '',
        address: '',
        number_of_users: 0,
        daily_cv_processing_limit: 0,
        daily_job_offer_limit: 0,
        subscription_plan: '',
        subscription_duration_type: 'days',
        subscription_duration_days: 0,
        subscription_deadline: undefined,
        ai_provider: 'ollama',
        ai_ollama_host: 'https://ollama.com/',
        ai_openrouter_host: 'https://openrouter.ai/api/v1',
        ai_model: 'qwen3-coder:480b-cloud',
        ai_model_ocr: 'devstral-small-2:24b-cloud',
        ai_token: '',
        use_default_ai_token: false,
        allow_external_cv_ai: false,
        system_email_language: 'fr',
        admin_email: '',
        admin_setup_mode: 'invitation',
        admin_password: '',
        admin_password_confirm: '',
        admin_name: ''
      })
      setDeadlineDate(null)
      setLogoFile(null)
      setLogoPreview(null)
      setNameError('')
      setDomainError('')
      setEmailError('')
      setAdminEmailError('')
      setAdminPasswordError('')
      setAdminPasswordConfirmError('')
      setShowAdminPassword(false)
      setShowAdminPasswordConfirm(false)
    }
  }, [isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    
    // Handle AI provider change - auto-fill fields
    if (name === 'ai_provider') {
      setFormData(prev => {
        const newData = { ...prev, ai_provider: value }
        if (value === 'ollama') {
          newData.ai_ollama_host = 'https://ollama.com/'
          newData.ai_model = 'qwen3-coder:480b-cloud'
          newData.ai_model_ocr = 'devstral-small-2:24b-cloud'
        } else if (value === 'openrouter') {
          newData.ai_openrouter_host = 'https://openrouter.ai/api/v1'
          newData.ai_model = 'google/gemini-2.5-flash-lite'
          newData.ai_model_ocr = 'google/gemini-2.5-flash-lite'
        }
        return newData
      })
      return
    }
    
    // Handle synchronization between deadline and number of days
    if (name === 'subscription_duration_days') {
      const days = value === '' ? 0 : parseInt(value, 10)
      setFormData(prev => ({
        ...prev,
        subscription_duration_days: days
      }))
      
      // Sync deadline date when days change
      if (days > 0) {
        const today = new Date()
        today.setHours(0, 0, 0, 0) // Reset time to start of day
        const newDeadline = new Date(today)
        newDeadline.setDate(today.getDate() + days)
        setDeadlineDate(newDeadline)
        setFormData(prev => ({
          ...prev,
          subscription_deadline: newDeadline.toISOString().split('T')[0]
        }))
      } else {
        // If days is 0, clear the deadline
        setDeadlineDate(null)
        setFormData(prev => ({
          ...prev,
          subscription_deadline: undefined
        }))
      }
      return
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : parseInt(value, 10)) : value
    }))
    
    // Clear errors when user types, and validate domain in real-time (only if domain appears complete)
    if (name === 'name' && nameError) setNameError('')
    if (name === 'domain') {
      // Real-time domain validation - only validate if domain looks complete (has a dot)
      if (value && value.trim()) {
        const domainLower = value.toLowerCase().trim()
        const basicDomainRegex = /^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/
        
        // Basic format check
        if (domainLower.startsWith('.') || domainLower.endsWith('.') || 
            domainLower.startsWith('-') || domainLower.endsWith('-') || 
            domainLower.includes('..') || !basicDomainRegex.test(domainLower)) {
          setDomainError(t('organizations.validation.invalidDomainFormat', 'Domain can only contain lowercase letters, numbers, dots, and hyphens. Cannot start or end with dot or hyphen, and cannot have consecutive dots.'))
        } else {
          const parts = domainLower.split('.')
          // Only validate structure if domain has at least one dot (appears complete)
          if (parts.length >= 2) {
            const domainPart = parts.slice(0, -1).join('.')
            const tld = parts[parts.length - 1]
            
            if (!domainPart || domainPart.length === 0) {
              setDomainError(t('organizations.validation.domainPartRequired', 'Domain must have a name before the top-level domain'))
            } else if (tld.length < 2) {
              setDomainError(t('organizations.validation.invalidTld', 'Top-level domain must be at least 2 characters (e.g., .com, .org)'))
            } else if (!/^[a-z]{2,}$/.test(tld)) {
              setDomainError(t('organizations.validation.invalidTldFormat', 'Top-level domain must contain only letters'))
            } else {
              // Check each part
              const allParts = domainLower.split('.')
              let partsValid = true
              for (const part of allParts) {
                if (part.length === 0 || part.startsWith('-') || part.endsWith('-')) {
                  partsValid = false
                  break
                }
              }
              
              if (!partsValid) {
                setDomainError(t('organizations.validation.invalidDomainParts', 'Each part of the domain must not start or end with a hyphen'))
              } else {
                setDomainError('')
              }
            }
          } else {
            // Domain doesn't have a dot yet, clear error (user is still typing)
            setDomainError('')
          }
        }
      } else {
        // Empty domain, clear error
        setDomainError('')
      }
    }
    if (name === 'email' && emailError) setEmailError('')
    if (name === 'admin_email' && adminEmailError) setAdminEmailError('')
    if (name === 'admin_password' && adminPasswordError) setAdminPasswordError('')
    if (name === 'admin_password_confirm' && adminPasswordConfirmError) setAdminPasswordConfirmError('')
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const validateStep = (step: number): boolean => {
    if (step === STEPS.BASIC_INFO) {
      let isValid = true
      
      if (!formData.name.trim()) {
        setNameError(t('organizations.validation.nameRequired', 'Organization name is required'))
        isValid = false
      } else {
        setNameError('')
      }
      
      if (!formData.domain || !formData.domain.trim()) {
        setDomainError(t('organizations.validation.domainRequired', 'Domain is required'))
        isValid = false
      } else {
        // Validate domain format as a real domain name
        const domainLower = formData.domain.toLowerCase().trim()
        
        // Basic format check: alphanumeric, hyphens, dots - but not starting/ending with dot or hyphen
        const basicDomainRegex = /^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/
        
        // Check for invalid patterns
        if (domainLower.startsWith('.') || domainLower.endsWith('.') || 
            domainLower.startsWith('-') || domainLower.endsWith('-') || 
            domainLower.includes('..') || !basicDomainRegex.test(domainLower)) {
          setDomainError(t('organizations.validation.invalidDomainFormat', 'Domain can only contain lowercase letters, numbers, dots, and hyphens. Cannot start or end with dot or hyphen, and cannot have consecutive dots.'))
          isValid = false
        } else {
          // Validate as a real domain name structure
          // Must contain at least one dot to separate domain and TLD
          // Must have at least one character before the dot (domain part)
          // Must have at least 2 characters after the last dot (TLD)
          const parts = domainLower.split('.')
          
          if (parts.length < 2) {
            setDomainError(t('organizations.validation.domainNeedsTld', 'Domain must include a top-level domain (e.g., example.com)'))
            isValid = false
          } else {
            // Check that domain part (before last dot) is not empty
            const domainPart = parts.slice(0, -1).join('.')
            const tld = parts[parts.length - 1]
            
            if (!domainPart || domainPart.length === 0) {
              setDomainError(t('organizations.validation.domainPartRequired', 'Domain must have a name before the top-level domain'))
              isValid = false
            } else if (tld.length < 2) {
              setDomainError(t('organizations.validation.invalidTld', 'Top-level domain must be at least 2 characters (e.g., .com, .org)'))
              isValid = false
            } else if (!/^[a-z]{2,}$/.test(tld)) {
              setDomainError(t('organizations.validation.invalidTldFormat', 'Top-level domain must contain only letters'))
              isValid = false
            } else {
              // Additional validation: each part between dots should be valid
              const allParts = domainLower.split('.')
              let partsValid = true
              for (const part of allParts) {
                if (part.length === 0 || part.startsWith('-') || part.endsWith('-')) {
                  partsValid = false
                  break
                }
              }
              
              if (!partsValid) {
                setDomainError(t('organizations.validation.invalidDomainParts', 'Each part of the domain must not start or end with a hyphen'))
                isValid = false
              } else {
                setDomainError('')
              }
            }
          }
        }
      }
      
      return isValid
    }

    if (step === STEPS.CONTACT_INFO) {
      // Contact info is optional, but validate email format if provided
      let isValid = true
      
      if (formData.email && formData.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formData.email)) {
          setEmailError(t('organizations.validation.invalidEmail', 'Invalid email format'))
          isValid = false
        } else {
          setEmailError('')
        }
      } else {
        setEmailError('')
      }
      
      return isValid
    }

    if (step === STEPS.SUBSCRIPTION_LIMITS) {
      // Subscription and limits are optional, no validation needed
      return true
    }

    if (step === STEPS.AI_SETTINGS) {
      // AI settings are optional, no validation needed
      return true
    }

    if (step === STEPS.ADMIN_ACCOUNT) {
      let isValid = true
      
      if (!formData.admin_email?.trim()) {
        setAdminEmailError(t('organizations.validation.adminEmailRequired', 'Admin email is required'))
        isValid = false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email.trim())) {
        setAdminEmailError(t('organizations.validation.invalidEmail', 'Invalid email format'))
        isValid = false
      } else {
        setAdminEmailError('')
      }

      if (formData.admin_setup_mode === 'credentials') {
        if (!formData.admin_password?.trim()) {
          setAdminPasswordError(t('organizations.validation.adminPasswordRequired', 'Password is required'))
          isValid = false
        } else if (formData.admin_password.length < 8) {
          setAdminPasswordError(t('organizations.validation.adminPasswordMinLength', 'Password must be at least 8 characters'))
          isValid = false
        } else {
          setAdminPasswordError('')
        }

        if (!formData.admin_password_confirm?.trim()) {
          setAdminPasswordConfirmError(t('organizations.validation.adminPasswordConfirmRequired', 'Please confirm the password'))
          isValid = false
        } else if (formData.admin_password !== formData.admin_password_confirm) {
          setAdminPasswordConfirmError(t('organizations.validation.adminPasswordMismatch', 'Passwords do not match'))
          isValid = false
        } else {
          setAdminPasswordConfirmError('')
        }
      } else {
        setAdminPasswordError('')
        setAdminPasswordConfirmError('')
      }
      
      return isValid
    }

    return true
  }

  const handleNext = () => {
    const isValid = validateStep(currentStep)
    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1))
    } else {
      // Scroll to first error if needed
      const firstErrorElement = document.querySelector('.border-destructive')
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const handleSubmit = async () => {
    // Validate all steps before submitting
    let isValid = true
    for (let i = 0; i < totalSteps; i++) {
      if (!validateStep(i)) {
        isValid = false
        setCurrentStep(i)
        break
      }
    }

    if (!isValid) {
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('organizations.validation.pleaseCompleteAllSteps', 'Please complete all required fields'),
        duration: 4000
      })
      return
    }

    try {
      setLoading(true)
      const createData: CreateOrganizationData = {
        ...formData,
        subscription_deadline: deadlineDate ? deadlineDate.toISOString().split('T')[0] : undefined,
        logo: logoFile || undefined
      }
      const response = await organizationsApi.create(createData)
      
      addToast({
        type: 'success',
        title: t('common.success'),
        message: response.data.admin_created
          ? t('organizations.messages.createdWithAdmin', 'Organization created and admin account is ready to use')
          : response.data.invitation_email_sent === false
            ? t('organizations.messages.createdWithoutEmail', 'Organization created, but the invitation email could not be sent. Check SMTP settings or resend the invitation.')
            : t('organizations.messages.created', 'Organization created and invitation email sent'),
        duration: 5000
      })
      
      onOrganizationCreated(response.data)
      onClose()
    } catch (error: any) {
      console.error('Error creating organization:', error)
      const errorData = error?.response?.data
      
      // Handle field-specific errors
      if (errorData) {
        if (errorData.name) {
          const errorMsg = Array.isArray(errorData.name) ? errorData.name[0] : errorData.name
          setNameError(errorMsg)
          setCurrentStep(STEPS.BASIC_INFO)
        }
        if (errorData.domain) {
          const errorMsg = Array.isArray(errorData.domain) ? errorData.domain[0] : errorData.domain
          setDomainError(errorMsg)
          setCurrentStep(STEPS.BASIC_INFO)
        }
        if (errorData.email) {
          const errorMsg = Array.isArray(errorData.email) ? errorData.email[0] : errorData.email
          setEmailError(errorMsg)
          setCurrentStep(STEPS.CONTACT_INFO)
        }
        if (errorData.admin_email) {
          const errorMsg = Array.isArray(errorData.admin_email) ? errorData.admin_email[0] : errorData.admin_email
          setAdminEmailError(errorMsg)
          setCurrentStep(STEPS.ADMIN_ACCOUNT)
        }
        if (errorData.admin_password) {
          const errorMsg = Array.isArray(errorData.admin_password) ? errorData.admin_password[0] : errorData.admin_password
          setAdminPasswordError(errorMsg)
          setCurrentStep(STEPS.ADMIN_ACCOUNT)
        }
        if (errorData.admin_password_confirm) {
          const errorMsg = Array.isArray(errorData.admin_password_confirm) ? errorData.admin_password_confirm[0] : errorData.admin_password_confirm
          setAdminPasswordConfirmError(errorMsg)
          setCurrentStep(STEPS.ADMIN_ACCOUNT)
        }
      }
      
      addToast({
        type: 'error',
        title: t('common.error'),
        message: errorData?.error || errorData?.detail || t('organizations.messages.createFailed', 'Failed to create organization'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setCurrentStep(0)
      onClose()
    }
  }

  const renderStepIndicator = () => {
    return (
      <div className="relative mb-8">
        <div className="flex items-start">
          {stepConfig.map((step, index) => {
            const isActive = index === currentStep
            const isCompleted = index < currentStep
            const Icon = step.icon
            const isLast = index === stepConfig.length - 1

            return (
              <React.Fragment key={index}>
                <div className="flex flex-col items-center flex-1 relative">
                  {/* Step Circle */}
                  <div className="relative z-10">
                    <div
                      className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                        isActive
                          ? 'bg-primary text-primary-foreground border-primary'
                          : isCompleted
                          ? 'bg-primary/10 text-primary border-primary'
                          : 'bg-background text-muted-foreground border-muted'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Icon className={`w-5 h-5 ${isActive ? 'text-primary-foreground' : ''}`} />
                      )}
                    </div>
                  </div>
                  
                  {/* Step Label */}
                  <div className="mt-3 text-center max-w-[140px] px-2">
                    <div className={`text-sm font-semibold ${
                      isActive ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.name}
                    </div>
                    <div className={`text-xs mt-1 ${
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.description}
                    </div>
                  </div>
                </div>
                
                {/* Connector Line */}
                {!isLast && (
                  <div className="flex-1 relative mx-2" style={{ height: '48px' }}>
                    <div className="absolute left-0 right-0 h-0.5 bg-muted" style={{ top: '50%', transform: 'translateY(-50%)' }} />
                    <div 
                      className={`absolute left-0 h-0.5 ${
                        isCompleted ? 'bg-primary' : 'bg-transparent'
                      }`}
                      style={{ 
                        width: isCompleted ? '100%' : '0%',
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    )
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.BASIC_INFO:
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  {t('organizations.form.name', 'Organization Name')} <span className="text-red-500">*</span>
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('organizations.form.namePlaceholder', 'Enter organization name')}
                  disabled={loading}
                  aria-invalid={!!nameError}
                  className={nameError ? 'border-destructive focus:ring-destructive' : ''}
                />
                {nameError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{nameError}</p>}
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  {t('organizations.form.description', 'Description')}
                </label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  placeholder={t('organizations.form.descriptionPlaceholder', 'Enter organization description')}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    {t('organizations.form.domain', 'Domain')} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="domain"
                    value={formData.domain || ''}
                    onChange={handleInputChange}
                    placeholder={t('organizations.form.domainPlaceholder', 'e.g., bvr-organization')}
                    disabled={loading}
                    aria-invalid={!!domainError}
                    className={domainError ? 'border-destructive focus:ring-destructive' : ''}
                    required
                  />
                  {domainError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{domainError}</p>}
                  
                </div>

                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <LinkIcon className="w-4 h-4 text-muted-foreground" />
                    {t('organizations.form.website', 'Website')}
                  </label>
                  <Input
                    name="website"
                    type="url"
                    value={formData.website || ''}
                    onChange={handleInputChange}
                    placeholder={t('organizations.form.websitePlaceholder', 'https://example.com')}
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  {t('organizations.form.logo', 'Logo')}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    disabled={loading}
                    id="logo-upload"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <div className="flex items-center justify-between w-full px-3 py-2 border rounded-md bg-background text-foreground">
                    <span className="text-sm text-muted-foreground">
                      {logoFile 
                        ? logoFile.name 
                        : t('organizations.form.noFileChosen', 'No file chosen')}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault()
                        document.getElementById('logo-upload')?.click()
                      }}
                      disabled={loading}
                      className="pointer-events-none"
                    >
                      {t('organizations.form.chooseFile', 'Choose File')}
                    </Button>
                  </div>
                </div>
                {logoPreview && (
                  <div className="mt-2">
                    <img src={logoPreview} alt="Logo preview" className="h-20 w-20 object-contain border rounded" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )

      case STEPS.CONTACT_INFO:
        return (
          <div className="space-y-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-1">{t('organizations.form.contactInfo', 'Contact Information')}</h3>
              <p className="text-sm text-muted-foreground">{t('organizations.form.contactInfoDesc', 'Provide contact details for the organization')}</p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {t('organizations.form.email', 'Email')}
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email || ''}
                    onChange={handleInputChange}
                    placeholder={t('organizations.form.emailPlaceholder', 'contact@organization.com')}
                    disabled={loading}
                    aria-invalid={!!emailError}
                    className={emailError ? 'border-destructive focus:ring-destructive' : ''}
                  />
                  {emailError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{emailError}</p>}
                </div>

                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    {t('organizations.form.phone', 'Phone')}
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone || ''}
                    onChange={handleInputChange}
                    placeholder={t('organizations.form.phonePlaceholder', '+1234567890')}
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  {t('organizations.form.address', 'Address')}
                </label>
                <textarea
                  name="address"
                  value={formData.address || ''}
                  onChange={handleInputChange}
                  placeholder={t('organizations.form.addressPlaceholder', 'Enter organization address')}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
                  disabled={loading}
                />
              </div>

              <div className="border-t pt-4 space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">
                    {t('organizations.form.systemEmailLanguageTitle')}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('organizations.form.systemEmailLanguageDescription')}
                  </p>
                </div>
                <div className="max-w-md space-y-2">
                  <label className="text-sm font-medium block">
                    {t('organizations.form.systemEmailLanguageLabel')}
                  </label>
                  <select
                    name="system_email_language"
                    value={formData.system_email_language || 'fr'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
                    disabled={loading}
                  >
                    <option value="fr">{t('organizations.form.systemEmailLanguageFrench')}</option>
                    <option value="en">{t('organizations.form.systemEmailLanguageEnglish')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )

      case STEPS.SUBSCRIPTION_LIMITS:
        return (
          <div className="space-y-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-1">{t('organizations.form.subscriptionAndLimits', 'Subscription & Limits')}</h3>
              <p className="text-sm text-muted-foreground">{t('organizations.form.subscriptionAndLimitsDesc', 'Configure subscription plan and processing limits')}</p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('organizations.form.numberOfUsers', 'Number of Users')}
                  </label>
                  <Input
                    name="number_of_users"
                    type="number"
                    value={formData.number_of_users || 0}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('organizations.form.dailyCvLimit', 'Daily CV Processing Limit')}
                  </label>
                  <Input
                    name="daily_cv_processing_limit"
                    type="number"
                    value={formData.daily_cv_processing_limit || 0}
                    onChange={handleInputChange}
                    placeholder="0 (unlimited)"
                    min="0"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('organizations.form.dailyOfferLimit', 'Daily Job Offer Limit')}
                  </label>
                  <Input
                    name="daily_job_offer_limit"
                    type="number"
                    value={formData.daily_job_offer_limit || 0}
                    onChange={handleInputChange}
                    placeholder="0 (unlimited)"
                    min="0"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('organizations.form.subscriptionPlan', 'Subscription Plan')}
                  </label>
                  <select
                    name="subscription_plan"
                    value={formData.subscription_plan || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
                    disabled={loading}
                  >
                    <option value="">{t('organizations.form.selectPlan', 'Select a plan')}</option>
                    <option value="starter">Starter</option>
                    <option value="premium">Premium</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('organizations.form.subscriptionDuration', 'Subscription Duration')}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <ModernDatePicker
                      selected={deadlineDate}
                      onChange={(date) => {
                        setDeadlineDate(date)
                        setFormData(prev => ({
                          ...prev,
                          subscription_deadline: date ? date.toISOString().split('T')[0] : undefined
                        }))
                        
                        // Sync number of days when deadline changes
                        if (date) {
                          const today = new Date()
                          today.setHours(0, 0, 0, 0) // Reset time to start of day
                          const deadline = new Date(date)
                          deadline.setHours(0, 0, 0, 0) // Reset time to start of day
                          const diffTime = deadline.getTime() - today.getTime()
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                          setFormData(prev => ({
                            ...prev,
                            subscription_duration_days: diffDays > 0 ? diffDays : 0
                          }))
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            subscription_duration_days: 0
                          }))
                        }
                      }}
                      placeholder={t('organizations.form.deadlinePlaceholder', 'Select deadline date')}
                      minDate={new Date()}
                      label={t('organizations.form.deadline', 'Deadline')}
                      smartPositioning={true}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">
                      {t('organizations.form.numberOfDays', 'Number of Days')}
                    </label>
                    <Input
                      name="subscription_duration_days"
                      type="number"
                      value={formData.subscription_duration_days || 0}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                      disabled={loading}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('organizations.form.subscriptionDurationHelp', 'Changing one field will automatically sync with the other')}
                </p>
              </div>
            </div>
          </div>
        )

      case STEPS.AI_SETTINGS:
        return (
          <div className="space-y-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-1">{t('organizations.form.aiSettings', 'AI Settings')}</h3>
              <p className="text-sm text-muted-foreground">{t('organizations.form.aiSettingsDesc', 'Configure AI provider and authentication for this organization')}</p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-muted-foreground" />
                    {t('organizations.form.aiProvider', 'AI Provider')}
                  </label>
                  <select
                    name="ai_provider"
                    value={formData.ai_provider || 'ollama'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md bg-background text-foreground"
                    disabled={loading}
                  >
                    <option value="ollama">Ollama</option>
                    <option value="openrouter">OpenRouter</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('organizations.form.aiModel', 'AI Model')}
                  </label>
                  <Input
                    name="ai_model"
                    value={formData.ai_model || ''}
                    onChange={handleInputChange}
                    placeholder={t('organizations.form.aiModelPlaceholder', 'qwen3-coder:480b-cloud')}
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('organizations.form.aiModelOcr', 'AI Model OCR/Vision')}
                  </label>
                  <Input
                    name="ai_model_ocr"
                    value={formData.ai_model_ocr || ''}
                    onChange={handleInputChange}
                    placeholder={t('organizations.form.aiModelOcrPlaceholder', 'devstral-small-2:24b-cloud')}
                    disabled={loading}
                  />
                </div>

                {formData.ai_provider === 'ollama' && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-2 block">
                      {t('organizations.form.aiOllamaHost', 'Ollama Host')}
                    </label>
                    <Input
                      name="ai_ollama_host"
                      type="url"
                      value={formData.ai_ollama_host || ''}
                      onChange={handleInputChange}
                      placeholder={t('organizations.form.aiOllamaHostPlaceholder', 'https://ollama.com/')}
                      disabled={loading}
                    />
                  </div>
                )}

                {formData.ai_provider === 'openrouter' && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-2 block">
                      {t('organizations.form.aiOpenRouterHost', 'OpenRouter Host')}
                    </label>
                    <Input
                      name="ai_openrouter_host"
                      type="url"
                      value={formData.ai_openrouter_host || ''}
                      onChange={handleInputChange}
                      placeholder={t('organizations.form.aiOpenRouterHostPlaceholder', 'https://openrouter.ai/api/v1')}
                      disabled={loading}
                    />
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4 text-muted-foreground" />
                    {t('organizations.form.aiToken', 'AI Token')}
                  </label>
                  <label className="flex items-center gap-2 mb-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.use_default_ai_token)}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setFormData((prev) => ({ ...prev, use_default_ai_token: checked }))
                      }}
                      disabled={loading}
                      className="h-4 w-4 rounded border-input accent-primary cursor-pointer disabled:cursor-not-allowed"
                    />
                    {t('organizations.form.useDefaultAiToken', 'Use default token')}
                  </label>
                  {formData.use_default_ai_token ? (
                    <Input
                      type="password"
                      value="••••••••••••••••••••••••"
                      readOnly
                      disabled
                      aria-label={t('organizations.form.useDefaultAiToken', 'Use default token')}
                    />
                  ) : (
                    <Input
                      name="ai_token"
                      type="password"
                      value={formData.ai_token || ''}
                      onChange={handleInputChange}
                      placeholder={t('organizations.form.aiTokenPlaceholder', 'Enter AI service token (optional)')}
                      disabled={loading}
                    />
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.use_default_ai_token
                      ? t('organizations.form.useDefaultAiTokenHelp', 'The platform default AI token will be assigned to this organization. Uncheck to paste your own token.')
                      : t('organizations.form.aiTokenHelp', 'Optional: Authentication token for AI services')}
                  </p>
                </div>

                <div className="md:col-span-2 rounded-md border border-border bg-muted/30 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium block">
                        {t(
                          'organizations.form.allowExternalCvAi',
                          'Allow sending CVs to external AI'
                        )}
                      </label>
                      <p className="text-xs text-muted-foreground">
                        {t(
                          'organizations.form.allowExternalCvAiHelp',
                          'When enabled, candidate CV text and images may be sent to non-local AI hosts (e.g. Ollama Cloud, OpenRouter). Local AI (localhost / private network) always works without this.'
                        )}
                      </p>
                    </div>
                    <Switch
                      checked={Boolean(formData.allow_external_cv_ai)}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({ ...prev, allow_external_cv_ai: checked }))
                      }
                      disabled={loading}
                      aria-label={t(
                        'organizations.form.allowExternalCvAi',
                        'Allow sending CVs to external AI'
                      )}
                    />
                  </div>
                  {formData.allow_external_cv_ai && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      {t(
                        'organizations.form.allowExternalCvAiWarning',
                        'You confirm that sending candidate personal data to this external AI provider is allowed for this organization (e.g. contract / DPA in place).'
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )

      case STEPS.ADMIN_ACCOUNT:
        return (
          <div className="space-y-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-1">{t('organizations.form.adminAccount', 'Admin Account')}</h3>
              <p className="text-sm text-muted-foreground">{t('organizations.form.adminAccountDesc', 'Choose how to set up the organization administrator')}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  admin_setup_mode: 'invitation',
                  admin_password: '',
                  admin_password_confirm: '',
                }))}
                disabled={loading}
                className={`text-left p-4 rounded-lg border-2 transition-colors ${
                  formData.admin_setup_mode === 'invitation'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Send className="w-4 h-4 text-primary" />
                  <span className="font-medium text-sm">{t('organizations.form.adminSetupInvitation', 'Send invitation link')}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('organizations.form.adminSetupInvitationDesc', 'Email a secure link for the admin to set their password')}
                </p>
              </button>

              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, admin_setup_mode: 'credentials' }))}
                disabled={loading}
                className={`text-left p-4 rounded-lg border-2 transition-colors ${
                  formData.admin_setup_mode === 'credentials'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <KeyRound className="w-4 h-4 text-primary" />
                  <span className="font-medium text-sm">{t('organizations.form.adminSetupCredentials', 'Create login credentials')}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('organizations.form.adminSetupCredentialsDesc', 'Set the admin email and password now')}
                </p>
              </button>
            </div>

            <div className="space-y-4">
              {formData.admin_setup_mode === 'credentials' ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        {t('organizations.form.adminName', 'Admin Name')}
                      </label>
                      <Input
                        name="admin_name"
                        type="text"
                        value={formData.admin_name || ''}
                        onChange={handleInputChange}
                        placeholder={t('organizations.form.adminNamePlaceholder', 'John Doe')}
                        disabled={loading}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('organizations.form.adminNameHelp', 'Optional display name for the admin account')}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        {t('organizations.form.adminEmail', 'Admin Email')} <span className="text-red-500">*</span>
                      </label>
                      <Input
                        name="admin_email"
                        type="email"
                        value={formData.admin_email || ''}
                        onChange={handleInputChange}
                        placeholder={t('organizations.form.adminEmailPlaceholder', 'admin@company.com')}
                        disabled={loading}
                        aria-invalid={!!adminEmailError}
                        className={adminEmailError ? 'border-destructive focus:ring-destructive' : ''}
                      />
                      {adminEmailError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{adminEmailError}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <KeyRound className="w-4 h-4 text-muted-foreground" />
                        {t('organizations.form.adminPassword', 'Password')} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Input
                          name="admin_password"
                          type={showAdminPassword ? 'text' : 'password'}
                          value={formData.admin_password || ''}
                          onChange={handleInputChange}
                          placeholder={t('organizations.form.adminPasswordPlaceholder', 'Enter password')}
                          disabled={loading}
                          aria-invalid={!!adminPasswordError}
                          className={`${adminPasswordError ? 'border-destructive focus:ring-destructive' : ''} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowAdminPassword(prev => !prev)}
                          disabled={loading}
                          aria-label={showAdminPassword ? 'Hide password' : 'Show password'}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                        >
                          {showAdminPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {adminPasswordError && <p className="mt-1 text-sm text-destructive">{adminPasswordError}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('organizations.form.adminPasswordHelp', 'Minimum 8 characters')}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Lock className="w-4 h-4 text-muted-foreground" />
                        {t('organizations.form.adminPasswordConfirm', 'Confirm Password')} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Input
                          name="admin_password_confirm"
                          type={showAdminPasswordConfirm ? 'text' : 'password'}
                          value={formData.admin_password_confirm || ''}
                          onChange={handleInputChange}
                          placeholder={t('organizations.form.adminPasswordConfirmPlaceholder', 'Confirm password')}
                          disabled={loading}
                          aria-invalid={!!adminPasswordConfirmError}
                          className={`${adminPasswordConfirmError ? 'border-destructive focus:ring-destructive' : ''} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowAdminPasswordConfirm(prev => !prev)}
                          disabled={loading}
                          aria-label={showAdminPasswordConfirm ? 'Hide password' : 'Show password'}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                        >
                          {showAdminPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {adminPasswordConfirmError && <p className="mt-1 text-sm text-destructive">{adminPasswordConfirmError}</p>}
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {t('organizations.form.adminEmail', 'Admin Email')} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="admin_email"
                    type="email"
                    value={formData.admin_email || ''}
                    onChange={handleInputChange}
                    placeholder={t('organizations.form.adminEmailPlaceholder', 'admin@company.com')}
                    disabled={loading}
                    aria-invalid={!!adminEmailError}
                    className={adminEmailError ? 'border-destructive focus:ring-destructive' : ''}
                  />
                  {adminEmailError && <p className="mt-1 text-sm text-destructive flex items-center gap-1">{adminEmailError}</p>}
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('organizations.form.adminEmailHelp', 'A secure invitation link will be emailed and expires in 24 hours.')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  const footer = (
    <div className="flex justify-between items-center">
      <Button 
        variant="outline" 
        onClick={currentStep === 0 ? handleClose : handlePrevious}
        disabled={loading}
        className="min-w-[100px]"
      >
        <ChevronLeft className="w-4 h-4 mr-2" />
        {currentStep === 0 ? t('common.cancel', 'Cancel') : t('common.previous', 'Previous')}
      </Button>
      
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>{t('common.step', 'Step')} {currentStep + 1} {t('common.of', 'of')} {totalSteps}</span>
      </div>
      
      <div className="flex space-x-3">
        {currentStep < totalSteps - 1 ? (
          <Button 
            onClick={handleNext}
            disabled={loading}
            className="min-w-[100px]"
          >
            {t('common.next', 'Next')}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit}
            disabled={loading || !formData.name.trim()}
            className="min-w-[120px]"
          >
            {loading ? (
              t('common.creating', 'Creating...')
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {t('organizations.create', 'Create Organization')}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('organizations.create', 'Create Organization')}
      footer={footer}
      className="max-w-3xl"
    >
      <div className="space-y-6">
        {renderStepIndicator()}
        
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>
      </div>
    </Modal>
  )
}

